/**
 * Attendance Cache - Store attendance records for offline support
 * When offline: Store locally in IndexedDB
 * When online: Service Worker syncs with backend
 */

const DB_NAME = 'SmartAttendanceDB';
const DB_VERSION = 2;
const ATTENDANCE_STORE = 'pendingAttendance';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains(ATTENDANCE_STORE)) {
        const store = db.createObjectStore(ATTENDANCE_STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('schoolCode', 'schoolCode', { unique: false });
      }
    };
  });
}

/**
 * Save attendance record locally for sync later
 * Called when recording attendance (online or offline)
 */
export async function saveAttendanceLocally(attendanceData) {
  try {
    const db = await openDB();
    const transaction = db.transaction([ATTENDANCE_STORE], 'readwrite');
    const store = transaction.objectStore(ATTENDANCE_STORE);
    
    const record = {
      ...attendanceData,
      timestamp: Date.now(),
      synced: false, // Not synced yet
      syncAttempts: 0
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(record);
      request.onsuccess = () => {
        console.log('[AttendanceCache] Record saved locally, ID:', request.result);
        resolve({ success: true, id: request.result });
      };
      request.onerror = () => {
        console.error('[AttendanceCache] Failed to save:', request.error);
        reject(request.error);
      };
    });
  } catch (err) {
    console.error('Error saving attendance locally:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get all unsynced attendance records
 */
export async function getUnsyncedAttendance() {
  try {
    const db = await openDB();
    const transaction = db.transaction([ATTENDANCE_STORE], 'readonly');
    const store = transaction.objectStore(ATTENDANCE_STORE);
    const index = store.index('synced');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(false);
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (err) {
    console.error('Error getting unsynced attendance:', err);
    return [];
  }
}

/**
 * Mark attendance record as synced
 */
export async function markAttendanceSynced(attendanceId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([ATTENDANCE_STORE], 'readwrite');
    const store = transaction.objectStore(ATTENDANCE_STORE);
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(attendanceId);
      getRequest.onsuccess = () => {
        const record = getRequest.result;
        if (record) {
          record.synced = true;
          record.syncedAt = Date.now();
          const updateRequest = store.put(record);
          updateRequest.onsuccess = () => resolve({ success: true });
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Record not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (err) {
    console.error('Error marking attendance synced:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Delete synced attendance records older than X days
 */
export async function deleteSyncedAttendanceOlderThan(days = 7) {
  try {
    const db = await openDB();
    const transaction = db.transaction([ATTENDANCE_STORE], 'readwrite');
    const store = transaction.objectStore(ATTENDANCE_STORE);
    
    const cutoffTime = Date.now() - (days * 24 * 60 * 60 * 1000);
    const range = IDBKeyRange.upperBound(cutoffTime);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(range);
      request.onsuccess = () => resolve({ success: true });
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.error('Error deleting old records:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get all synced and unsynced records (for debugging)
 */
export async function getAllAttendanceRecords() {
  try {
    const db = await openDB();
    const transaction = db.transaction([ATTENDANCE_STORE], 'readonly');
    const store = transaction.objectStore(ATTENDANCE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.error('Error getting all records:', err);
    return [];
  }
}

/**
 * Clear all attendance records (for reset/debugging)
 */
export async function clearAllAttendanceRecords() {
  try {
    const db = await openDB();
    const transaction = db.transaction([ATTENDANCE_STORE], 'readwrite');
    const store = transaction.objectStore(ATTENDANCE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve({ success: true });
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.error('Error clearing records:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Update sync attempts count
 */
export async function incrementSyncAttempts(attendanceId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([ATTENDANCE_STORE], 'readwrite');
    const store = transaction.objectStore(ATTENDANCE_STORE);
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(attendanceId);
      getRequest.onsuccess = () => {
        const record = getRequest.result;
        if (record) {
          record.syncAttempts = (record.syncAttempts || 0) + 1;
          record.lastSyncAttempt = Date.now();
          const updateRequest = store.put(record);
          updateRequest.onsuccess = () => resolve({ success: true });
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Record not found'));
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (err) {
    console.error('Error incrementing sync attempts:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get pending attendance count (for UI display)
 */
export async function getPendingAttendanceCount() {
  try {
    const records = await getUnsyncedAttendance();
    return records.length;
  } catch (err) {
    console.error('Error getting pending count:', err);
    return 0;
  }
}

/**
 * Trigger manual sync via Service Worker
 */
export function triggerManualSync() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_ATTENDANCE'
    });
    console.log('[AttendanceCache] Manual sync triggered');
  }
}
